import React, { useState, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../../supabase/supabase';

const {
  FiX, FiUser, FiMapPin, FiLink, FiInstagram,
  FiTwitter, FiCamera, FiSave, FiLoader, FiCheck, FiMusic,
} = FiIcons;

const inputCls = [
  'w-full rounded-2xl px-5 py-3.5 text-sm font-bold text-white',
  'placeholder-zinc-600 focus:outline-none transition-all',
  'bg-white/5 border border-white/8 focus:border-pink-500/40 focus:bg-white/8',
].join(' ');

const Field = ({ label, icon, children, hint }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
        {icon && <SafeIcon icon={icon} className="text-xs" style={{ color: '#ff66cc' }} />}
        {label}
      </label>
      {hint && <span className="text-[10px] text-zinc-700 font-bold">{hint}</span>}
    </div>
    {children}
  </div>
);

const GENRES = [
  'Electronic','Hip-Hop','R&B','Pop','Rock',
  'Jazz','Ambient','House','Techno','Experimental','Indie','Classical',
];

const FanProfileEditor = ({ profile, userId, onClose, onSaved }) => {
  const [form, setForm] = useState({
    display_name:    profile?.display_name    || '',
    username:        profile?.username        || '',
    bio:             profile?.bio             || '',
    location:        profile?.location        || '',
    website:         profile?.website         || '',
    instagram:       profile?.instagram       || '',
    twitter:         profile?.twitter         || '',
    favorite_genres: profile?.favorite_genres || [],
    avatar_url:      profile?.avatar_url      || '',
  });
  const [loading,       setLoading]       = useState(false);
  const [saved,         setSaved]         = useState(false);
  const [err,           setErr]           = useState('');
  const [avatarPreview, setAvatarPreview] = useState(profile?.avatar_url || '');
  const fileInputRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleGenre = (g) =>
    set('favorite_genres', form.favorite_genres.includes(g)
      ? form.favorite_genres.filter(x => x !== g)
      : [...form.favorite_genres, g]);

  const handleAvatarFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAvatarPreview(ev.target.result);
      set('avatar_url', ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.display_name.trim()) { setErr('Display name is required.'); return; }
    setLoading(true); setErr('');
    try {
      const { error } = await supabase
        .from('d2f_profiles')
        .update({
          display_name:    form.display_name.trim(),
          username:        form.username.trim() || null,
          bio:             form.bio.trim() || null,
          location:        form.location.trim() || null,
          website:         form.website.trim() || null,
          instagram:       form.instagram.trim() || null,
          twitter:         form.twitter.trim() || null,
          favorite_genres: form.favorite_genres,
          avatar_url:      form.avatar_url || null,
          updated_at:      new Date().toISOString(),
        })
        .eq('id', userId);
      if (error) throw error;
      setSaved(true);
      setTimeout(() => { setSaved(false); onSaved?.(); onClose(); }, 1400);
    } catch (e) {
      setErr(e.message || 'Failed to save profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(18px)', padding: '1rem' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl flex flex-col rounded-[2rem] overflow-hidden"
        style={{
          background: 'rgba(14,14,16,0.98)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
          maxHeight: 'calc(100vh - 2rem)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-6"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: '#ff66cc' }}>Account</p>
            <h3 className="text-2xl font-black text-white uppercase tracking-tight mt-0.5">Edit Profile</h3>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <SafeIcon icon={FiX} className="text-lg text-zinc-400" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-8 py-7 space-y-6">

          {/* Avatar */}
          <div className="flex items-center gap-6">
            <div className="relative group flex-shrink-0">
              <div className="w-24 h-24 rounded-3xl overflow-hidden"
                style={{ border: '2px solid rgba(255,102,204,0.3)', background: '#18181b' }}>
                {avatarPreview
                  ? <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg,rgba(255,102,204,0.15),rgba(255,102,0,0.08))' }}>
                      <SafeIcon icon={FiUser} className="text-3xl" style={{ color: 'rgba(255,102,204,0.4)' }} />
                    </div>
                }
              </div>
              <button onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 rounded-3xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: 'rgba(0,0,0,0.65)' }}>
                <SafeIcon icon={FiCamera} className="text-white text-xl" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarFile} />
            </div>
            <div className="flex-1 space-y-2">
              <p className="text-sm font-black text-white uppercase tracking-tight">Profile Photo</p>
              <p className="text-xs font-bold text-zinc-600">Hover avatar and click to upload.</p>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-zinc-300 hover:text-white transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  Upload Photo
                </button>
                {avatarPreview && (
                  <button onClick={() => { setAvatarPreview(''); set('avatar_url', ''); }}
                    className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-zinc-600 hover:text-red-400 transition-all"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <hr style={{ borderColor: 'rgba(255,255,255,0.05)' }} />

          {/* Name + Handle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Display Name *" icon={FiUser}>
              <input className={inputCls} placeholder="Your name"
                value={form.display_name} onChange={e => set('display_name', e.target.value)} />
            </Field>
            <Field label="Username" icon={FiUser} hint="@handle">
              <div className="relative">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-600 text-sm font-black">@</span>
                <input className={`${inputCls} pl-9`} placeholder="yourhandle"
                  value={form.username}
                  onChange={e => set('username', e.target.value.toLowerCase().replace(/\s+/g, '_'))} />
              </div>
            </Field>
          </div>

          {/* Bio */}
          <Field label="Bio" hint={`${form.bio.length}/160`}>
            <textarea className={`${inputCls} resize-none`} rows={3} maxLength={160}
              placeholder="Tell artists and fellow fans about yourself…"
              value={form.bio} onChange={e => set('bio', e.target.value)} />
          </Field>

          {/* Location + Website */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Location" icon={FiMapPin}>
              <input className={inputCls} placeholder="City, Country"
                value={form.location} onChange={e => set('location', e.target.value)} />
            </Field>
            <Field label="Website" icon={FiLink}>
              <input className={inputCls} placeholder="https://yoursite.com"
                value={form.website} onChange={e => set('website', e.target.value)} />
            </Field>
          </div>

          <hr style={{ borderColor: 'rgba(255,255,255,0.05)' }} />

          {/* Social */}
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Social Links</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Instagram" icon={FiInstagram}>
              <div className="relative">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-600 text-sm font-black">@</span>
                <input className={`${inputCls} pl-9`} placeholder="username"
                  value={form.instagram} onChange={e => set('instagram', e.target.value)} />
              </div>
            </Field>
            <Field label="Twitter / X" icon={FiTwitter}>
              <div className="relative">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-600 text-sm font-black">@</span>
                <input className={`${inputCls} pl-9`} placeholder="username"
                  value={form.twitter} onChange={e => set('twitter', e.target.value)} />
              </div>
            </Field>
          </div>

          <hr style={{ borderColor: 'rgba(255,255,255,0.05)' }} />

          {/* Genres */}
          <Field label="Favourite Genres" icon={FiMusic} hint="pick any">
            <div className="flex flex-wrap gap-2 mt-1">
              {GENRES.map(g => {
                const active = form.favorite_genres.includes(g);
                return (
                  <button key={g} type="button" onClick={() => toggleGenre(g)}
                    className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                    style={active
                      ? { background: 'linear-gradient(135deg,#ff66cc,#ff6600)', color: '#fff', boxShadow: '0 0 10px rgba(255,102,204,0.3)' }
                      : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#71717a' }
                    }>
                    {g}
                  </button>
                );
              })}
            </div>
          </Field>

          {err && (
            <p className="text-red-400 text-xs font-black uppercase tracking-widest text-center py-3 rounded-2xl"
              style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)' }}>
              {err}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-5"
          style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <button onClick={onClose}
            className="px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={loading || saved}
            className="flex items-center gap-2 px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-60"
            style={saved
              ? { background: '#22c55e', color: '#fff' }
              : { background: 'linear-gradient(135deg,#ff66cc,#ff6600)', color: '#fff', boxShadow: '0 4px 20px rgba(255,102,204,0.35)' }
            }>
            {loading && <SafeIcon icon={FiLoader} className="animate-spin text-sm" />}
            {saved   && <SafeIcon icon={FiCheck}  className="text-sm" />}
            {saved ? 'Saved!' : loading ? 'Saving…' : <><SafeIcon icon={FiSave} className="text-sm" /> Save Profile</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FanProfileEditor;